<template>
    <div class="space-y-2">
        <form-select name="Command" :model-value="action.commandId" @update:model-value="selectCommand">
            <option v-if="!categories" value="">Select a command</option>
            <optgroup v-for="group in commandGroups" :key="group.category" :label="group.category">
                <option v-for="command in group.commands" :key="command.id" :value="command.id">
                    {{ command.label }}
                </option>
            </optgroup>
        </form-select>

        <div v-for="parameter in definition?.parameters ?? []" :key="parameter.id" class="flex items-center gap-2">
            <form-label class="w-2/5 shrink-0">{{ parameter.label }}</form-label>
            <select v-if="parameter.type === 'page'"
                class="flex-1 rounded border border-gray-400 bg-white px-1 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                :value="action.parameters?.[parameter.id] ?? ''"
                @change="setParameter(parameter.id, $event.target.value)">
                <option value="">Select a page</option>
                <option v-for="page in availablePages" :key="page.id" :value="page.id">{{ page.label }}</option>
            </select>
            <form-input v-else-if="parameter.type === 'text'" class="flex-1 min-w-0"
                :name="parameter.label"
                :model-value="action.parameters?.[parameter.id] ?? parameter.default ?? ''"
                @update:model-value="setTextParameter(parameter.id, $event)" />
            <button v-else class="flex-1 truncate rounded border border-gray-400 bg-white px-2 py-1 text-left text-sm text-gray-900 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                @click="openParameter(parameter.id)">
                <span v-if="action.parameters?.[parameter.id]">{{ action.parameters[parameter.id] }}</span>
                <span v-else class="text-gray-500">Set value…</span>
            </button>
        </div>

        <p v-if="generatedCommand" class="break-all text-xs text-gray-400">
            Generated: <code>{{ generatedCommand }}</code>
        </p>
        <p v-if="!commands.length" class="text-xs italic text-gray-400">No unused commands available</p>

        <ResourcePicker v-if="action.commandId === 'audio.play'" resourceType="audio"
            :modelValue="action.resourceId ?? ''" @update:modelValue="updateResource" />
        <IconPicker v-model="showBuilder" :initial-text="parameterText"
            :available-tabs="['icons', 'variables']" :extra-variables="valueVariables"
            :preview-values="previewValues" @text-updated="updateParameter" />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { getCommandsForContext } from '../../../actions/commandContext.js';
import { getEventContextVariables } from '../../../actions/actionContextResolver.js';
import { getCommandDefinition } from '../../../actions/commandRegistry.js';
import { initialCommandParameters } from '../../../actions/commandParser.js';
import { serializeCommandAction } from '../../../actions/commandSerializer.js';
import ResourcePicker from '../../resources/ResourcePicker.vue';
import IconPicker from '../../IconPicker.vue';

const props = defineProps({
    node: { type: Object, required: true }, handler: { type: Object, required: true },
    action: { type: Object, required: true },
    // All handlers of the source object; defaults to its stored actions.
    handlers: { type: Array, default: null },
    // Restrict the choice to these command categories (a logic node's category).
    categories: { type: Array, default: null },
});
const emit = defineEmits(['change']);
const showBuilder = ref(false);
const activeParameter = ref('');
const commands = computed(() => getCommandsForContext({
    sourceNode: props.node, event: props.handler.event, handlers: props.handlers ?? props.node.actions,
    currentActionId: props.action.id,
}));
const commandGroups = computed(() => {
    const groups = commands.value
        .filter(command => !props.categories || props.categories.includes(command.category))
        .reduce((result, command) => {
            const category = command.category ?? 'Other';
            (result[category] ??= []).push(command);
            return result;
        }, {});
    return Object.entries(groups).map(([category, groupedCommands]) => ({ category, commands: groupedCommands }));
});
const definition = computed(() => getCommandDefinition(props.action.commandId));
const generatedCommand = computed(() => serializeCommandAction(props.action));
const availablePages = computed(() => (props.node.getLayer?.()?.getChildren?.() ?? [])
    .filter(page => page.obj === 'page')
    .map(page => Number(page.haspid))
    .filter(pageId => Number.isInteger(pageId) && pageId > 0)
    .sort((a, b) => a - b)
    .map(pageId => ({ id: pageId, label: `p${pageId}` })));
const parameterText = computed(() => props.action.parameters?.[activeParameter.value] ?? '');
const valueVariables = computed(() => getEventContextVariables(props.node, props.handler.event));
const previewValues = computed(() => Object.fromEntries(valueVariables.value.map(variable =>
    [variable.id, variable.preview])));
function selectCommand(commandId) {
    props.action.commandId = commandId;
    props.action.parameters = initialCommandParameters(commandId);
    delete props.action.command;
    delete props.action.resourceId;
    emit('change');
}
function openParameter(parameterId) {
    activeParameter.value = parameterId;
    showBuilder.value = true;
}
function updateParameter(value) {
    props.action.parameters ??= {};
    props.action.parameters[activeParameter.value] = value;
    emit('change');
}
function setParameter(parameterId, value) {
    props.action.parameters ??= {};
    props.action.parameters[parameterId] = value === '' ? '' : Number(value);
    emit('change');
}
function setTextParameter(parameterId, value) {
    props.action.parameters ??= {};
    props.action.parameters[parameterId] = value;
    emit('change');
}
function updateResource(value) {
    props.action.resourceId = value;
    emit('change');
}
</script>
